import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { apiService, DASHBOARD_AUTH_CHANGED_EVENT, getDashboardToken } from '@/api/apiService';

interface AdminOnlyRouteProps {
  children: ReactNode;
}

const AdminOnlyRoute = ({ children }: AdminOnlyRouteProps) => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isAllowed, setIsAllowed] = useState(false);

  useEffect(() => {
    let mounted = true;

    const refresh = async () => {
      const token = getDashboardToken();
      if (!token) {
        if (!mounted) return;
        setIsAllowed(false);
        setIsLoading(false);
        return;
      }

      if (mounted) setIsLoading(true);
      try {
        const payload = await apiService.dashboardMe();
        if (!mounted) return;
        setIsAllowed(Boolean(payload?.user?.is_platform_admin));
      } catch {
        if (!mounted) return;
        setIsAllowed(false);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    void refresh();
    const onAuthChanged = () => {
      void refresh();
    };
    window.addEventListener(DASHBOARD_AUTH_CHANGED_EVENT, onAuthChanged as EventListener);
    return () => {
      mounted = false;
      window.removeEventListener(DASHBOARD_AUTH_CHANGED_EVENT, onAuthChanged as EventListener);
    };
  }, [location.pathname]);

  if (isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Checking access...</div>;
  }

  if (!getDashboardToken()) {
    return <Navigate to="/dashboard" replace state={{ from: location.pathname }} />;
  }

  if (!isAllowed) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default AdminOnlyRoute;
